// src/components/ChunkLoadErrorBoundary.js
// [PURPOSE]: Catches ChunkLoadError from lazy(() => import(...)) when a
//   user's cached HTML references a chunk that no longer exists after a
//   Vercel redeploy. Shows a graceful "Refresh to load" message instead
//   of bubbling up to the global crash screen.
// [WHY]: Deploy-skew is a routine occurrence; users shouldn't see a full
//   crash screen for it. Pre-fix: clicking the AI button under the
//   character portrait after a fresh deploy triggered the global crash.
// [INTEGRATION]: Wrap individual <Suspense> blocks around lazy-loaded
//   components. Each can opt in to graceful chunk-error handling.

import React from 'react';

class ChunkLoadErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasChunkError: false, otherError: null };
  }

  static getDerivedStateFromError(error) {
    if (error?.name === 'ChunkLoadError' || /Loading chunk \d+ failed/.test(error?.message || '')) {
      return { hasChunkError: true, otherError: null };
    }
    return { hasChunkError: false, otherError: error };
  }

  componentDidCatch(error, info) {
    // Log to console but don't rethrow chunk errors. Other errors still propagate.
    if (this.state.otherError) throw error;
    console.warn('[ChunkLoadErrorBoundary] Caught chunk load failure:', error?.message);
  }

  render() {
    if (this.state.hasChunkError) {
      return (
        <div className="p-4 bg-amber-900/30 border border-amber-700 rounded-lg text-amber-100 text-sm">
          <div className="font-medium mb-2">Couldn't load this feature.</div>
          <div className="text-xs text-amber-200 mb-3">The app updated since you opened it. Refresh the page to load the latest version.</div>
          <button
            onClick={() => window.location.reload()}
            className="px-3 py-1.5 bg-amber-700 hover:bg-amber-600 rounded text-white text-sm transition-colors"
          >
            Refresh page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ChunkLoadErrorBoundary;
